import { Component } from '@theme/component';

/**
 * Notify Me Form Component
 *
 * Handles Klaviyo back-in-stock notification subscriptions and optional marketing list subscriptions.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} [notifyMeForm] - The form element.
 * @property {HTMLInputElement} [notifyMeVariantId] - Hidden input containing the variant ID.
 * @property {HTMLInputElement} [notifyMeEmailInput] - Email input field.
 * @property {HTMLButtonElement} [notifyMeSubmitButton] - Submit button.
 * @property {HTMLElement} [notifyMeFormWrapper] - Form wrapper container.
 * @property {HTMLElement} [notifyMeSuccessWrapper] - Success wrapper container.
 * @property {HTMLElement} [notifyMeErrorMessage] - Error message container.
 * @property {HTMLInputElement} [notifyMeMarketingConsent] - Optional marketing consent checkbox.
 * @property {HTMLElement} [notifyMeHeading] - Heading element.
 *
 * @extends Component<Refs>
 */
class NotifyMeFormComponent extends Component {
  /** @type {'ready' | 'submitting' | 'submitted' | 'error'} */
  state = 'ready';

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #submitTimeoutId = null;

  /** @type {'timeout' | null} */
  #abortReason = null;

  /** @type {(event: MouseEvent) => void} */
  #boundHandleSubmit = this.#handleSubmit.bind(this);

  /** @type {(event: MouseEvent) => void} */
  #boundHandleSubmitCapture = this.#handleSubmitCapture.bind(this);

  /** @type {() => void} */
  #boundHandleDialogClose = this.reset.bind(this);

  /** @type {() => void} */
  #boundHandleDialogOpen = this.#focusEmailInput.bind(this);

  /** @type {Element | null} */
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();
    // Use capture-phase delegation so we handle the click even if refs weren't
    // available at connect time (e.g. dialog content or refs updated later)
    this.addEventListener('click', this.#boundHandleSubmitCapture, true);

    // Listen for dialog events
    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener('dialog:close', this.#boundHandleDialogClose);
    this.#dialogComponent?.addEventListener('dialog:open', this.#boundHandleDialogOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#boundHandleSubmitCapture, true);
    this.#dialogComponent?.removeEventListener('dialog:close', this.#boundHandleDialogClose);
    this.#dialogComponent?.removeEventListener('dialog:open', this.#boundHandleDialogOpen);
    this.#dialogComponent = null;
    // Abort any pending requests
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Gets the Klaviyo API key from data attribute.
   * @returns {string}
   */
  get apiKey() {
    return this.dataset.klaviyoApiKey || '';
  }

  /**
   * Gets the Klaviyo API version from data attribute.
   * @returns {string}
   */
  get apiVersion() {
    return this.dataset.klaviyoApiVersion || '2024-06-15';
  }

  /**
   * Gets the Klaviyo list ID from data attribute.
   * @returns {string}
   */
  get listId() {
    return this.dataset.klaviyoListId || '';
  }

  /**
   * Capture-phase handler: only delegate to #handleSubmit when the click target
   * is the submit button (so we don't rely on refs at connect time).
   * @param {MouseEvent} event - The click event.
   */
  #handleSubmitCapture(event) {
    const submitBtn = this.refs.notifyMeSubmitButton ?? this.querySelector('[ref="notifyMeSubmitButton"]');
    if (submitBtn && (event.target === submitBtn || submitBtn.contains(/** @type {Node} */ (event.target)))) {
      this.#handleSubmit(/** @type {MouseEvent} */ (event));
    }
  }

  /**
   * Handles form submission via button click.
   * @param {MouseEvent} event - The click event.
   */
  async #handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.state === 'submitting') {
      return;
    }

    const emailVal = this.refs.notifyMeEmailInput?.value?.trim();
    const variantIdVal = this.refs.notifyMeVariantId?.value;

    if (!emailVal || !variantIdVal) {
      return;
    }

    if (!this.apiKey) {
      console.error('Klaviyo API key is not configured');
      this.#showError();
      return;
    }

    this.state = 'submitting';
    this.#setSubmitButtonDisabled(true);
    this.#abortReason = null;

    // Create new AbortController for this submission
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    // Timeout so we don't hang forever if Klaviyo never responds
    this.#submitTimeoutId = setTimeout(() => {
      this.#abortReason = 'timeout';
      this.#abortController?.abort();
    }, 15000);

    try {
      await this.#submitBackInStockSubscription(emailVal, variantIdVal);

      if (this.#submitTimeoutId != null) {
        clearTimeout(this.#submitTimeoutId);
        this.#submitTimeoutId = null;
      }

      // Show success immediately after back-in-stock subscription succeeds
      this.state = 'submitted';
      this.#showSuccess();
      this.dataset.debugLast = JSON.stringify({ outcome: 'success', at: Date.now() });

      if (window.htevents) {
        window.htevents.track(
          'Notify Me When Available Submitted',
          {
            email: emailVal,
            shopify_variant_id: variantIdVal,
            shopify_product_id: this.dataset.productId || null,
            name: this.dataset.productTitle || null,
            sku: this.dataset.variantSku || null,
            variant: this.dataset.variantTitle || null,
            price: this.dataset.variantPrice ? (Number(this.dataset.variantPrice) / 100).toFixed(2) : null,
            compare_at_price: this.dataset.variantCompareAtPrice ? (Number(this.dataset.variantCompareAtPrice) / 100).toFixed(2) : null,
            category: this.dataset.productType || null,
            currency: window.Shopify?.currency?.active,
          },
          { sent_from: 'Hightouch JS' },
        )
        window.htevents.identify({ email: emailVal }, { sent_from: 'Hightouch JS' })
      }

      // Fire-and-forget marketing subscription - don't block the success UI
      const marketingConsent = this.refs.notifyMeMarketingConsent?.checked;
      if (marketingConsent && this.listId) {
        this.#subscribeToMarketing(emailVal);
      }
    } catch (error) {
      if (this.#submitTimeoutId != null) {
        clearTimeout(this.#submitTimeoutId);
        this.#submitTimeoutId = null;
      }
      const errName = error instanceof Error ? error.name : 'Unknown';
      const errMsg = error instanceof Error ? error.message : String(error);
      this.dataset.debugLast = JSON.stringify({ outcome: 'error', name: errName, message: errMsg, abortReason: this.#abortReason, at: Date.now() });

      // Ignore abort only when component disconnected (not timeout)
      if (errName === 'AbortError' && this.#abortReason !== 'timeout') {
        return;
      }
      console.error('Back in stock subscription failed:', error);
      this.state = 'error';
      this.#showError();
      this.#setSubmitButtonDisabled(false);
    }
  }

  /**
   * Submits a back-in-stock subscription to Klaviyo.
   * @param {string} email - The customer's email address.
   * @param {string} variantId - The Shopify variant ID.
   * @returns {Promise<Response>}
   */
  async #submitBackInStockSubscription(email, variantId) {
    const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.apiKey}`;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/vnd.api+json',
        'Revision': this.apiVersion,
      },
      body: JSON.stringify(payload),
      signal: this.#abortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Klaviyo API error: ${response.status}`);
    }

    return response;
  }

  /**
   * Subscribes the email to a Klaviyo marketing list.
   * @param {string} email - The customer's email address.
   * @returns {Promise<Response | void>}
   */
  async #subscribeToMarketing(email) {
    const endpoint = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.apiKey}`;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
          custom_source: 'Back in Stock Form',
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Revision': this.apiVersion,
        },
        body: JSON.stringify(payload),
        // No abort signal - fire and forget
      });

      if (!response.ok) {
        console.warn(`Marketing subscription failed: ${response.status}`);
      }

      return response;
    } catch (error) {
      // Silently ignore marketing subscription errors
      console.warn('Marketing subscription failed:', error);
    }
  }

  /**
   * Sets the submit button disabled state.
   * @param {boolean} disabled - Whether the button should be disabled.
   */
  #setSubmitButtonDisabled(disabled) {
    const btn = this.refs.notifyMeSubmitButton ?? /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="notifyMeSubmitButton"]'));
    if (btn) btn.disabled = disabled;
  }

  /**
   * Shows the success state - hides form wrapper and shows success wrapper.
   * Uses querySelector fallback so UI updates even if refs were stale.
   */
  #showSuccess() {
    const formWrapper = this.refs.notifyMeFormWrapper ?? this.querySelector('[ref="notifyMeFormWrapper"]');
    const successWrapper = this.refs.notifyMeSuccessWrapper ?? this.querySelector('[ref="notifyMeSuccessWrapper"]');
    formWrapper?.classList.add('hidden');
    successWrapper?.classList.remove('hidden');
  }

  /**
   * Shows the error message.
   * Uses querySelector fallback so UI updates even if refs were stale.
   */
  #showError() {
    const errorEl = this.refs.notifyMeErrorMessage ?? this.querySelector('[ref="notifyMeErrorMessage"]');
    errorEl?.classList.remove('hidden');
  }

  /**
   * Resets the form to its initial state.
   */
  reset() {
    this.state = 'ready';
    const formWrapper = this.refs.notifyMeFormWrapper ?? this.querySelector('[ref="notifyMeFormWrapper"]');
    const successWrapper = this.refs.notifyMeSuccessWrapper ?? this.querySelector('[ref="notifyMeSuccessWrapper"]');
    const errorEl = this.refs.notifyMeErrorMessage ?? this.querySelector('[ref="notifyMeErrorMessage"]');
    formWrapper?.classList.remove('hidden');
    successWrapper?.classList.add('hidden');
    errorEl?.classList.add('hidden');
    // Clear email input manually since form element is stripped by browser (nested forms)
    const emailInput = this.refs.notifyMeEmailInput ?? /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="notifyMeEmailInput"]'));
    const marketingConsent = this.refs.notifyMeMarketingConsent ?? /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="notifyMeMarketingConsent"]'));
    if (emailInput) emailInput.value = '';
    if (marketingConsent) marketingConsent.checked = false;
    this.#setSubmitButtonDisabled(false);
  }

  /**
   * Focuses the email input field when the dialog opens.
   */
  #focusEmailInput() {
    this.refs.notifyMeEmailInput?.focus();
  }

  /**
   * Delegates to parent dialog-component's closeDialog method.
   * Used by on:click="/closeDialog" on buttons inside this component.
   */
  closeDialog() {
    /** @type {any} */
    const dialogComponent = this.#dialogComponent;
    dialogComponent?.closeDialog?.();
  }
}

if (!customElements.get('notify-me-form-component')) {
  customElements.define('notify-me-form-component', NotifyMeFormComponent);
}

/**
 * @typedef {object} NotifyMeProductData
 * @property {string} [productId]
 * @property {string} [productTitle]
 * @property {string} [productType]
 * @property {string} [variantSku]
 * @property {string} [variantTitle]
 * @property {string|number} [variantPrice]
 * @property {string|number} [variantCompareAtPrice]
 */

/**
 * Opens the global notify-me drawer and populates it with variant data.
 * @param {string} variantId - The Shopify variant ID
 * @param {string} [sizeLabel] - The size label to display in the description
 * @param {NotifyMeProductData} [productData] - Optional product data for Hightouch tracking
 */
window.openNotifyMeDrawer = function openNotifyMeDrawer(variantId, sizeLabel, productData) {
  /** @type {any} */
  const drawerComponent = document.querySelector('#notify-me-drawer');
  if (!drawerComponent) return;

  const variantInput = drawerComponent.querySelector('[ref="notifyMeVariantId"]');
  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = variantId;
  }

  const variantTitle = drawerComponent.querySelector('[ref="notifyMeVariantTitle"]');
  if (variantTitle && sizeLabel) {
    variantTitle.textContent =
      `Size ${sizeLabel} is currently unavailable. Enter your email to receive an email notification when this item is available.`;
  }

  // Populate product data attributes on the form component for Hightouch tracking.
  const formComponent = drawerComponent.querySelector('notify-me-form-component');
  if (formComponent instanceof HTMLElement && productData) {
    if (productData.productId != null) formComponent.dataset.productId = String(productData.productId);
    if (productData.productTitle != null) formComponent.dataset.productTitle = productData.productTitle;
    if (productData.productType != null) formComponent.dataset.productType = productData.productType;
    if (productData.variantSku != null) formComponent.dataset.variantSku = productData.variantSku;
    if (productData.variantTitle != null) formComponent.dataset.variantTitle = productData.variantTitle;
    if (productData.variantPrice != null) formComponent.dataset.variantPrice = String(productData.variantPrice);
    if (productData.variantCompareAtPrice != null) formComponent.dataset.variantCompareAtPrice = String(productData.variantCompareAtPrice);
  }

  drawerComponent.showDialog?.();
};

document.addEventListener('click', (e) => {
  const trigger = /** @type {Element | null} */ (e.target)?.closest('[data-open-notify-me]');
  if (!trigger) return;

  const variantId = trigger.getAttribute('data-variant-id') || '';
  const sizeLabel = trigger.getAttribute('data-size-label') || '';
  if (variantId) {
    window.openNotifyMeDrawer?.(variantId, sizeLabel, {
      productId: trigger.getAttribute('data-product-id') || undefined,
      productTitle: trigger.getAttribute('data-product-title') || undefined,
      productType: trigger.getAttribute('data-product-type') || undefined,
      variantSku: trigger.getAttribute('data-variant-sku') || undefined,
      variantTitle: trigger.getAttribute('data-variant-title') || undefined,
      variantPrice: trigger.getAttribute('data-variant-price') || undefined,
      variantCompareAtPrice: trigger.getAttribute('data-variant-compare-at-price') || undefined,
    });
  }
});

export { NotifyMeFormComponent };
